import ModelVarsCsvReader from "@components/ModelVarsCsvReader";
import MediaVarCell from "@components/Table/MediaVarCell";
import ModelVarInputCell from "@components/Table/ModelVarInputCell";
import StyledTableCell from "@components/Table/TableCell";
import StyledTableRow from "@components/Table/TableRow";
import { ClientContext } from "@context/Client/ClientContext";
import AddIcon from "@mui/icons-material/Add";
import SwipeIcon from "@mui/icons-material/Swipe";
import {
  Box,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useClientModels } from "@services/api";
import { CustomClientModelVariable } from "@services/api/types";
import { IVariableValues } from "@utils/external_effects";
import { getLastMediaVarId } from "@utils/media_vars";
import { TFunction } from "i18next";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo
} from "react";
import { useTranslation } from "react-i18next";

const _totalRow = (
  model_id: string,
  timePeriods: string[],
  variableValues: IVariableValues,
  t: TFunction<"translation", undefined>
) => {
  return (
    <StyledTableRow
      key={`new-${model_id}-total`}
      sx={{
        "&:last-child td, &:last-child th": { border: 0 }
      }}
    >
      <StyledTableCell
        align="center"
        key={`new-scenario-media-total-cell-${model_id}-total-channel`}
      >
        {t("simulator.scenario.total")}
      </StyledTableCell>
      {timePeriods.map((timePeriod) => {
        const timePeriodTotalValue =
          timePeriod in variableValues
            ? Object.keys(variableValues[timePeriod]).reduce(
              (acc, variable) =>
                acc + variableValues[timePeriod][Number(variable)],
              0
            )
            : 0;
        return (
          <StyledTableCell
            align="center"
            key={`new-scenario-media-total-cell-${model_id}-${timePeriod}`}
          >
            {timePeriodTotalValue}
          </StyledTableCell>
        );
      })}
      <StyledTableCell
        align="center"
        key={`new-scenario-media-total-cell-${model_id}-total`}
      >
        {timePeriods
          .map((timePeriod) =>
            timePeriod in variableValues
              ? Object.keys(variableValues[timePeriod]).reduce(
                (acc, variable) =>
                  acc + variableValues[timePeriod][Number(variable)],
                0
              )
              : 0
          )
          .reduce((acc, timePeriodValue) => acc + timePeriodValue, 0)}
      </StyledTableCell>
    </StyledTableRow>
  );
};

export interface StepExternalVarsProps {
  timePeriods: string[];
  variableValues: IVariableValues;
  setVariableValues: Dispatch<SetStateAction<IVariableValues>>;
  mediaVars: CustomClientModelVariable[];
  setMediaVars: Dispatch<SetStateAction<CustomClientModelVariable[]>>;
}
const StepMediaVars = ({
  timePeriods,
  variableValues,
  setVariableValues,
  mediaVars,
  setMediaVars
}: StepExternalVarsProps) => {
  const { currentClient } = useContext(ClientContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const iconVisibility = isTablet ? "" : "hidden";
  const iconJustify = isMobile ? "justify-left" : "justify-center";

  const { data: models, isFetching: isLoadingModels } = useClientModels(
    currentClient.client.id.toString()
  );

  const modelName: string = useMemo(() => {
    return (
      (models || []).find(
        (model) => model.id.toString() === currentClient.model.id.toString()
      )?.name || currentClient.model.id.toString()
    );
  }, [models, currentClient.model.id]);

  const handleNumericInputChange = useCallback(
    (timePeriod: string, varOder: number, value: any) => {
      variableValues[timePeriod][varOder] = Number(value as string);
      setVariableValues({ ...variableValues });
    },
    [setVariableValues, variableValues]
  );

  const handleSwitchInputChange = useCallback(
    (timePeriod: string, varOder: number, value: boolean) => {
      variableValues[timePeriod][varOder] = value ? 1 : 0;
      setVariableValues({ ...variableValues });
    },
    [setVariableValues, variableValues]
  );

  const handleAddMediaModelVar = () => {
    const lastIndex = getLastMediaVarId(mediaVars);
    setMediaVars([
      ...mediaVars,
      {
        id: lastIndex + 1,
        name: "",
        is_significant: false,
        order: lastIndex + 1,
        type: "media",
        color: theme.palette.warning.main,
        input_type: "input",
        is_custom: true
      }
    ]);
  };

  const handleEditMediaModelVar = useCallback(
    (mediaVarId: number, name: string) => {
      const othermediaVars = mediaVars.filter(
        (mediaVar) => mediaVar.id !== mediaVarId
      );
      const editedMediaVar = mediaVars.find(
        (mediaVar) => mediaVar.id === mediaVarId
      );
      if (!editedMediaVar) {
        return;
      }
      setMediaVars([
        ...othermediaVars,
        {
          ...editedMediaVar,
          name
        }
      ]);
    },
    [mediaVars]
  );

  const handleDeleteMediaModelVar = useCallback(
    (index: number) => {
      const newMediaVars = mediaVars.filter(
        (mediaVar) => !mediaVar.is_custom || mediaVar.order !== index
      );
      setMediaVars([...newMediaVars]);
    },
    [setMediaVars, mediaVars]
  );

  const tableHeaders = useMemo(
    () =>
      timePeriods.map((timePeriod) => (
        <StyledTableCell
          align="center"
          key={`new-scenario-media-var-${timePeriod}`}
        >
          {timePeriod}
        </StyledTableCell>
      )),
    [timePeriods]
  );

  const tableRows = useMemo(() => {
    const modelVarsRows = mediaVars.map((modelVar) => {
      const isInputType = modelVar.input_type === "input";
      const modelVarTotal = timePeriods
        .map((timePeriod) =>
          timePeriod in variableValues &&
            modelVar.id in variableValues[timePeriod]
            ? variableValues[timePeriod][modelVar.id]
            : 0
        )
        .reduce((acc, timePeriodValue) => acc + timePeriodValue, 0);
      return (
        <StyledTableRow
          key={`new-${currentClient.model.id.toString()}-${modelVar.id}`}
          sx={{
            "&:last-child td, &:last-child th": { border: 0 }
          }}
        >
          <StyledTableCell align="center">
            <MediaVarCell
              key={`new-scenario-media-var-cell-${modelVar.id}`}
              modelVar={modelVar}
              handleDeleteMediaVar={handleDeleteMediaModelVar}
              handleEditMediaModelVar={handleEditMediaModelVar}
            />
          </StyledTableCell>
          {timePeriods.map((timePeriod) => {
            const modelVarValue =
              timePeriod in variableValues &&
                modelVar.id in variableValues[timePeriod]
                ? variableValues[timePeriod][modelVar.id]
                : 0;
            return (
              <ModelVarInputCell
                onChangeInput={(event) => {
                  if (isInputType) {
                    handleNumericInputChange(
                      timePeriod,
                      modelVar.id,
                      event.target.value
                    );
                  } else {
                    const eventSwitch = event as ChangeEvent<HTMLInputElement>;
                    handleSwitchInputChange(
                      timePeriod,
                      modelVar.id,
                      eventSwitch.target.checked
                    );
                  }
                }}
                isInputType={isInputType}
                modelVarValue={modelVarValue}
                key={`new-scenario-media-cell-${timePeriod}-${modelVar.id}`}
              />
            );
          })}
          <StyledTableCell
            align="center"
            key={`new-scenario-media-total-cell-${modelVar.id}`}
          >
            {modelVarTotal}
          </StyledTableCell>
        </StyledTableRow>
      );
    });
    const totalRow = _totalRow(
      currentClient.model.id.toString(),
      timePeriods,
      variableValues,
      t
    );

    return [...modelVarsRows, totalRow];
  }, [
    t,
    handleNumericInputChange,
    handleSwitchInputChange,
    handleDeleteMediaModelVar,
    timePeriods,
    mediaVars,
    currentClient.model.id.toString(),
    variableValues
  ]);

  return (
    <Box className="w-full mt-8">
      <Box className="w-full ml-4">
        {isLoadingModels && <LinearProgress color="primary" className="mt-2" />}
        <ModelVarsCsvReader
          enabled={true}
          modelVars={mediaVars}
          timePeriods={timePeriods}
          templateFileName={`${modelName}_${timePeriods[0]}-${timePeriods[timePeriods.length - 1]}_template.csv`}
          setModelVars={setMediaVars}
          setVariableValues={setVariableValues}
          modelHeader="channel"
        >
          <Box className={`w-full flex pt-4 ${iconVisibility} ${iconJustify}`}>
            <SwipeIcon
              sx={{
                bottom: 12,
                opacity: 0.4,
                left: "50%",
                transform: "translate(750%, 0%)",
                color: theme.palette.primary.main
              }}
              fontSize="small"
            />
          </Box>
          <TableContainer
            className="w-full pt-4 pr-8 pb-4 overflow-y-scroll"
            sx={{
              maxHeight: "calc(100vh - 650px)",
              minHeight: 550,
              overflowX: isMobile ? "scroll" : "none", // Estilos para "quitar" la barra de scroll
              scrollbarWidth: "none", // Estilos para "quitar" la barra de scroll - Firefox
              "&::-webkit-scrollbar": {
                display: "none" // Estilos para "quitar" la barra de scroll - Chrome, Safari y Edge
              }
            }}
          >
            <Table
              className="w-full"
              aria-label="new scenario external vars table"
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell align="center">
                    {t("simulator.scenario.table.media_variables.channel")}
                  </StyledTableCell>
                  {tableHeaders}
                  <StyledTableCell align="center">
                    {t("simulator.scenario.total")}
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>{tableRows}</TableBody>
            </Table>
          </TableContainer>
          <Box className="w-full flex pl-4">
            <Button
              variant="contained"
              color="info"
              onClick={handleAddMediaModelVar}
            >
              <AddIcon />
            </Button>
          </Box>
          <Box className="w-full flex p-4">
            <Box sx={{ color: theme.palette.error.main, fontWeight: 600 }}>
              *
            </Box>
            <Box sx={{ fontStyle: "italic" }}>
              {t("simulator.scenario.significant_variables", {
                kpi: currentClient.client.kpi_label
              })}
            </Box>
          </Box>
        </ModelVarsCsvReader>
      </Box>
    </Box>
  );
};
export default StepMediaVars;
